import React, { useEffect, useMemo, useRef, useState } from "react";
import moment from "moment";
import { v4 as uuidv4 } from "uuid";
import { Icon } from "@/components/Icons";
import Button from "@/components/common/Button";
import Input from "@/components/common/Form/Input";
import GooglePlacesInput from "@/components/common/Form/GooglePlacesInput";
import { DatePicker } from "@/components/common/Form/Datepicker";
import {
  DocumentTypeId,
  GOOGLE_MAP_OPTIONS,
} from "@/configuration/globalVariable";
import { SnackbarUtils } from "@/utils";
import { AddressComponents } from "@/utils/address";
import { Wrapper } from "@googlemaps/react-wrapper";
import {
  useCheckDeliveryDate,
  useGetEmptyDocument,
  useProductLookup,
  useSaveDocument,
  enrichProducts,
} from "../services";
import {
  CustomerSearchItem,
  DocumentLineModel,
  DocumentModel,
  ProductSearchItem,
} from "../types";
import CustomerLookup, {
  CustomerLookupHandle,
} from "../components/CustomerLookup";
import ProductTable from "../components/ProductTable";

const createEmptyLine = (): DocumentLineModel => ({
  id: uuidv4(),
  productId: undefined,
  productSKU: "",
  productName: "",
  productDescription: "",
  unitOfMeasure: "",
  quantity: 0,
  sellPrice: 0,
  sellPriceBeforeTax: 0,
  taxCode: "",
  taxPercentage: 0,
  comment: "",
  costPrice: 0,
  isNew: true,
  isAdHoc: true,
});

const formatCurrency = (value?: number) =>
  Intl.NumberFormat("en-AU", {
    style: "currency",
    currency: "AUD",
    minimumFractionDigits: 2,
  }).format(value || 0);

const formatAddress = (
  doc: DocumentModel | null,
  type: "delivery" | "billing"
): string => {
  if (!doc) return "";

  const prefix = type === "delivery" ? "Delivery" : "Billing";
  const parts: string[] = [];

  const line1 = (doc as any)[`${prefix}AddressLine1`] as string | undefined;
  const line2 = (doc as any)[`${prefix}AddressLine2`] as string | undefined;
  const suburb = (doc as any)[`${prefix}Suburb`] as string | undefined;
  const state = (doc as any)[`${prefix}State`] as string | undefined;
  const postCode = (doc as any)[`${prefix}PostCode`] as string | undefined;
  const country = (doc as any)[`${prefix}Country`] as string | undefined;

  if (line1) parts.push(line1);
  if (line2) parts.push(line2);
  if (suburb) parts.push(suburb);
  if (state || postCode) {
    const statePostcode = [state, postCode].filter(Boolean).join(" ");
    if (statePostcode) parts.push(statePostcode);
  }
  if (country) parts.push(country);

  return parts.join(", ") || "";
};

const useDebounce = <T,>(value: T, delay = 400) => {
  const [debounced, setDebounced] = useState<T>(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debounced;
};

const normalizeLines = (items?: any[]): DocumentLineModel[] => {
  if (!items) return [];
  return items
    .filter(Boolean)
    .map((item) => ({
      id: item.id ?? uuidv4(),
      productId: item.productId,
      productSKU: item.productSKU ?? item.sku ?? "",
      productName: item.productName ?? item.name ?? "",
      productDescription: item.productDescription ?? item.description ?? "",
      unitOfMeasure: item.unitOfMeasure ?? item.unit ?? "",
      quantity: item.quantity != null ? Number(item.quantity) : 0,
      sellPrice: Number(item.sellPrice ?? item.price ?? 0),
      sellPriceBeforeTax:
        item.sellPriceBeforeTax ??
        (item.sellPrice && item.taxPercentage
          ? item.sellPrice / (1 + item.taxPercentage)
          : 0),
      taxCode: item.taxCode ?? item.tax ?? "",
      taxPercentage:
        item.taxPercentage != null ? Number(item.taxPercentage) * 100 : 0,
      comment: item.comment ?? "",
      costPrice: Number(item.costPrice ?? 0),
      isNew: item.isNew ?? true,
      isAdHoc: item.isAdHoc ?? false, // Pantry list items are not ad-hoc
    }))
    .filter(
      (line) =>
        // Keep lines that have a product name or SKU (pantry list items)
        line.productName || line.productSKU
    );
};

const EnterOrderForm = (props: { isIframe?: boolean }) => {
  const [selectedCustomer, setSelectedCustomer] =
    useState<CustomerSearchItem | null>(null);

  const [documentData, setDocumentData] = useState<DocumentModel | null>(null);
  const [pantryProductIds, setPantryProductIds] = useState<string[]>([]);
  const [favouriteProductIds, setFavouriteProductIds] = useState<string[]>([]);
  const [productAgreementIds, setProductAgreementIds] = useState<string[]>([]);
  const [isUseProductAgreement, setIsUseProductAgreement] = useState(false);
  const [deliveryDate, setDeliveryDate] = useState<Date | null>(() => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow;
  });
  const [isEditingDeliveryAddress, setIsEditingDeliveryAddress] =
    useState(false);
  const [editableDeliveryAddress, setEditableDeliveryAddress] = useState({
    line1: "",
    line2: "",
    suburb: "",
    state: "",
    postCode: "",
    country: "",
  });

  // Callback when Google Maps API is loaded
  const handleGoogleMapsLoad = () => {
    // Google Maps API is loaded - no need to track state
    // The GooglePlacesInput component will handle checking if API is available
  };

  // Exit edit mode if customer is cleared
  useEffect(() => {
    if (!selectedCustomer && isEditingDeliveryAddress) {
      setIsEditingDeliveryAddress(false);
    }
  }, [selectedCustomer, isEditingDeliveryAddress]);

  // Handle place selection from Google Places Autocomplete
  const handlePlaceSelect = (components: AddressComponents) => {
    setEditableDeliveryAddress({
      line1: components.addressLine1 || "",
      line2: components.addressLine2 || "",
      suburb: components.suburb || "",
      state: components.state || "",
      postCode: components.postcode || "",
      country: components.country || "",
    });
  };

  const [deliveryDateWarning, setDeliveryDateWarning] = useState<string | null>(
    null
  );
  const [note, setNote] = useState("");

  const [lines, setLines] = useState<DocumentLineModel[]>([createEmptyLine()]);
  const [productResults, setProductResults] = useState<ProductSearchItem[]>([]);
  const [activeProductRow, setActiveProductRow] = useState<number | null>(null);
  const [productSearchTerm, setProductSearchTerm] = useState("");
  const [idempotencyKey, setIdempotencyKey] = useState<string>(uuidv4()); // For duplicate prevention
  const [productDropdownPosition, setProductDropdownPosition] = useState<{
    top: number;
    left: number;
    width: number;
    openUpward: boolean;
  } | null>(null);
  const productInputRef = useRef<HTMLDivElement>(null);
  const [selectedProductIndex, setSelectedProductIndex] = useState<number>(0);
  const selectedProductRowRef = useRef<HTMLTableRowElement | null>(null);
  const justClosedDropdownRef = useRef<boolean>(false);
  const [showPantryList, setShowPantryList] = useState<boolean>(false);
  const [initialLineIds, setInitialLineIds] = useState<Set<string>>(new Set());
  const [previousCustomer, setPreviousCustomer] =
    useState<CustomerSearchItem | null>(null);
  const [focusedRowIndex, setFocusedRowIndex] = useState<number | null>(null);
  const inputRefs = useRef<{
    [key: string]: HTMLInputElement | null;
  }>({});
  const customerLookupRef = useRef<CustomerLookupHandle>(null);

  const getEmptyDocument = useGetEmptyDocument();
  const productLookup = useProductLookup();
  const checkDeliveryDate = useCheckDeliveryDate();
  const saveDocument = useSaveDocument();

  const debouncedProductTerm = useDebounce(productSearchTerm, 350);

  useEffect(() => {
    if (activeProductRow === null) return;

    const term = debouncedProductTerm.trim();
    if (!term || term.length < 2) {
      setProductResults([]);
      return;
    }

    // Customer must be selected before searching products
    const customerId =
      selectedCustomer?.customerAccountId ??
      selectedCustomer?.id ??
      documentData?.customerAccountId;

    if (!customerId) {
      setProductResults([]);
      return;
    }

    productLookup.mutate(
      {
        q: term,
        customerAccountId: customerId,
        page: 1,
      },
      {
        onSuccess: (data) => {
          // Enrich products with pantry and agreement data on client side
          const enrichedProducts = enrichProducts(
            data ?? [],
            pantryProductIds,
            favouriteProductIds,
            productAgreementIds,
            isUseProductAgreement
          );
          setProductResults(enrichedProducts);
        },
        onError: () => {
          SnackbarUtils.error("Unable to search products.");
        },
      }
    );
  }, [
    debouncedProductTerm,
    activeProductRow,
    selectedCustomer,
    documentData,
    pantryProductIds,
    favouriteProductIds,
    productAgreementIds,
    isUseProductAgreement,
  ]);

  // Calculate product dropdown position
  useEffect(() => {
    const updatePosition = () => {
      if (
        activeProductRow !== null &&
        productSearchTerm.trim().length >= 2 &&
        productInputRef.current
      ) {
        // Find the actual input element inside the ref
        const inputElement = productInputRef.current.querySelector("input");
        const containerRect = productInputRef.current.getBoundingClientRect();
        const rect = inputElement
          ? inputElement.getBoundingClientRect()
          : containerRect;

        // Dropdown height: max-h-56 = 224px
        const dropdownMaxHeight = 224;
        const spaceBelow = window.innerHeight - rect.bottom;
        const spaceAbove = rect.top;

        // Open upward if there's not enough space below and more space above
        const openUpward =
          spaceBelow < dropdownMaxHeight && spaceAbove > spaceBelow;

        setProductDropdownPosition({
          top: openUpward ? rect.top : rect.bottom + 1,
          left: rect.left,
          width: rect.width,
          openUpward,
        });
      } else {
        setProductDropdownPosition(null);
      }
    };

    updatePosition();

    // Update position on scroll and resize
    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);

    return () => {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [activeProductRow, productSearchTerm]);

  // Close product dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      const productInput = target.closest("[data-product-input]");
      const productDropdown = target.closest("[data-product-dropdown]");

      if (!productInput && !productDropdown && activeProductRow !== null) {
        setActiveProductRow(null);
        setProductResults([]);
        setProductDropdownPosition(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [activeProductRow]);

  const handleSelectCustomer = (customer: CustomerSearchItem) => {
    setSelectedCustomer(customer);

    // Immediately populate delivery address from customer data if available
    // This provides instant feedback before the API call completes
    if (customer.deliveryAddress) {
      const deliveryAddr = customer.deliveryAddress;
      setDocumentData((prev) => ({
        ...prev,
        DeliveryAddressLine1: deliveryAddr.street || "",
        DeliveryAddressLine2: "",
        DeliverySuburb: deliveryAddr.suburb || "",
        DeliveryState: deliveryAddr.state || "",
        DeliveryPostCode: deliveryAddr.postcode || "",
        DeliveryCountry: deliveryAddr.country || "",
        customerAccountId: customer.customerAccountId ?? customer.id,
      }));
    }

    getEmptyDocument.mutate(
      { customerId: customer.customerAccountId ?? customer.id, docType: 2 },
      {
        onSuccess: (res) => {
          if (res?.error) {
            SnackbarUtils.error(res?.message || "Unable to load order draft");
            return;
          }

          const doc = res?.doc ?? {};

          // Populate delivery address from customer data if available
          // This ensures we use the address from CustomerLookup if it exists
          if (customer.deliveryAddress) {
            const deliveryAddr = customer.deliveryAddress;
            doc.DeliveryAddressLine1 = deliveryAddr.street || "";
            doc.DeliveryAddressLine2 = "";
            doc.DeliverySuburb = deliveryAddr.suburb || "";
            doc.DeliveryState = deliveryAddr.state || "";
            doc.DeliveryPostCode = deliveryAddr.postcode || "";
            doc.DeliveryCountry = deliveryAddr.country || "";
          }

          // Store enrichment data for client-side product enrichment
          setPantryProductIds(res?.pantryProductIds || []);
          setFavouriteProductIds(res?.favouriteProductIds || []);
          setProductAgreementIds(res?.productAgreementIds || []);
          setIsUseProductAgreement(res?.isUseProductAgreement || false);

          setDocumentData(doc);
          setLines(() => {
            const mapped = normalizeLines(doc.documentLines);
            // Track initial line IDs from the document (pantry list products)
            const initialLineIdSet = new Set<string>(
              mapped
                .map((line) => line.id)
                .filter((id): id is string => Boolean(id))
            );
            setInitialLineIds(initialLineIdSet);
            // Always keep search row at the beginning
            return [createEmptyLine(), ...mapped];
          });

          setNote(doc?.note ?? "");
          if (doc?.deliveryDateFormatted) {
            const parsed = moment(doc.deliveryDateFormatted, "DD/MM/YYYY");
            setDeliveryDate(parsed.isValid() ? parsed.toDate() : null);
          } else {
            // Default to tomorrow
            const tomorrow = new Date();
            tomorrow.setDate(tomorrow.getDate() + 1);
            setDeliveryDate(tomorrow);
          }

          // Focus on the first product cell (search row) after loading customer data
          setTimeout(() => {
            if (productInputRef.current) {
              const inputElement =
                productInputRef.current.querySelector("input");
              if (inputElement) {
                inputElement.focus();
                setActiveProductRow(0);
              }
            }
          }, 100);
        },
      }
    );
  };

  const handleClearCustomer = () => {
    setSelectedCustomer(null);
    setDocumentData(null);
    setPantryProductIds([]);
    setFavouriteProductIds([]);
    setProductAgreementIds([]);
    setIsUseProductAgreement(false);
    setIsEditingDeliveryAddress(false);
    setEditableDeliveryAddress({
      line1: "",
      line2: "",
      suburb: "",
      state: "",
      postCode: "",
      country: "",
    });
    // Reset delivery date to tomorrow
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    setDeliveryDate(tomorrow);
    setDeliveryDateWarning(null);
    setNote("");
    // Reset lines to just the search row
    setLines([createEmptyLine()]);
  };

  const handleLineChange = (
    index: number,
    payload: Partial<DocumentLineModel>
  ) => {
    setLines((prev) =>
      prev.map((line, idx) => (idx === index ? { ...line, ...payload } : line))
    );
  };

  const handleProductPick = (product: ProductSearchItem) => {
    // Populate the search row (index 0) with product data instead of creating a new row
    setLines((prev) => {
      const updated = [...prev];
      updated[0] = {
        ...updated[0],
        productId: product.id,
        productSKU: product.sku ?? "",
        productName: product.name ?? "",
        productDescription: product.description ?? "",
        unitOfMeasure: product.unit ?? "",
        comment: product.comment ?? "",
        sellPrice: Number(product.price ?? 0),
        sellPriceBeforeTax:
          product.price && product.taxPercentage != null
            ? Number(product.price) / (1 + Number(product.taxPercentage))
            : undefined,
        taxCode: product.tax ?? product.taxCode ?? "",
        taxPercentage:
          product.taxPercentage != null
            ? Number(product.taxPercentage) * 100
            : undefined,
        isNew: true,
        isAdHoc: true, // Mark as manually added (ad-hoc)
        quantity: 0, // Keep quantity blank (displayed as empty string)
      };
      return updated;
    });

    setProductResults([]);
    setActiveProductRow(null); // Close dropdown after selection
    setProductSearchTerm("");
    setProductDropdownPosition(null);
    setSelectedProductIndex(0);

    // Set flag to prevent immediate reopening
    justClosedDropdownRef.current = true;
    setTimeout(() => {
      justClosedDropdownRef.current = false;
    }, 100);

    // Focus the quantity input in the search row
    setTimeout(() => {
      const quantityInput = inputRefs.current["0-quantity"];
      if (quantityInput) {
        quantityInput.focus();
        quantityInput.select();
      }
    }, 0);
  };

  const handleAddProduct = () => {
    const searchRow = lines[0];

    // Check if a product is selected
    if (!searchRow.productId) {
      SnackbarUtils.error("Please select a product");
      // Focus the product search box
      setTimeout(() => {
        if (productInputRef.current) {
          const inputElement = productInputRef.current.querySelector("input");
          if (inputElement) {
            inputElement.focus();
            inputElement.select();
          }
        }
      }, 0);
      return;
    }

    // Create new line with product data from search row
    // Allow quantity to be 0 or empty - validation happens only when saving
    const newLine: DocumentLineModel = {
      ...createEmptyLine(),
      productId: searchRow.productId,
      productSKU: searchRow.productSKU,
      productName: searchRow.productName,
      productDescription: searchRow.productDescription,
      unitOfMeasure: searchRow.unitOfMeasure,
      comment: searchRow.comment,
      sellPrice: searchRow.sellPrice,
      sellPriceBeforeTax: searchRow.sellPriceBeforeTax,
      taxCode: searchRow.taxCode,
      taxPercentage: searchRow.taxPercentage,
      quantity: searchRow.quantity,
      isNew: true,
      isAdHoc: true, // Mark as manually added (ad-hoc)
    };

    // Clear the search row and insert new product at position 1 (right after search row)
    setLines((prev) => {
      const updated = [...prev];
      // Clear the search row (index 0)
      updated[0] = createEmptyLine();
      // Insert new product at position 1
      updated.splice(1, 0, newLine);
      return updated;
    });

    // Focus the product search box
    setTimeout(() => {
      if (productInputRef.current) {
        const inputElement = productInputRef.current.querySelector("input");
        if (inputElement) {
          inputElement.focus();
          inputElement.select();
          setActiveProductRow(0);
        }
      }
    }, 0);
  };

  // Keyboard navigation handlers
  const handleQuantityKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    rowIndex: number
  ) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      let nextRowIndex: number | null = null;

      if (showPantryList) {
        // When pantry list is on, use simple navigation
        nextRowIndex = rowIndex + 1;
        if (nextRowIndex >= lines.length) {
          return;
        }
      } else {
        // When pantry list is off, navigate through filtered lines
        const searchRow = filteredLines[0];
        const productRows = filteredLines.slice(1);
        const displayLines = [searchRow, ...productRows];

        // Find current row's position in filtered lines
        const currentLine = lines[rowIndex];
        const currentFilteredIndex = displayLines.findIndex(
          (l) => l.id === currentLine.id
        );

        if (
          currentFilteredIndex >= 0 &&
          currentFilteredIndex < displayLines.length - 1
        ) {
          const nextFilteredLine = displayLines[currentFilteredIndex + 1];
          // Find original index of next filtered line
          nextRowIndex = lines.findIndex((l) => l.id === nextFilteredLine.id);
          if (nextRowIndex === -1) {
            return;
          }
        } else {
          return;
        }
      }

      const nextInput = inputRefs.current[`${nextRowIndex}-quantity`];
      if (nextInput) {
        nextInput.focus();
        nextInput.select();
      }
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      let prevRowIndex: number | null = null;

      if (showPantryList) {
        // When pantry list is on, use simple navigation
        prevRowIndex = rowIndex - 1;
        if (prevRowIndex < 0) {
          return;
        }
      } else {
        // When pantry list is off, navigate through filtered lines
        if (filteredLines.length === 0) return;

        const searchRow = filteredLines[0];
        const productRows = filteredLines.slice(1);
        const displayLines = [searchRow, ...productRows];

        // Find current row's position in filtered lines
        const currentLine = lines[rowIndex];
        if (!currentLine) return;

        const currentFilteredIndex = displayLines.findIndex(
          (l) => l.id === currentLine.id
        );

        if (currentFilteredIndex > 0) {
          const prevFilteredLine = displayLines[currentFilteredIndex - 1];
          // Find original index of previous filtered line
          prevRowIndex = lines.findIndex((l) => l.id === prevFilteredLine.id);
          if (prevRowIndex === -1) {
            return;
          }
        } else {
          return;
        }
      }

      const prevInput = inputRefs.current[`${prevRowIndex}-quantity`];
      if (prevInput) {
        prevInput.focus();
        prevInput.select();
      }
    } else if (e.key === "Enter") {
      e.preventDefault();
      // Focus on the product name input of the search row (new line row)
      if (productInputRef.current) {
        const inputElement = productInputRef.current.querySelector("input");
        if (inputElement) {
          inputElement.focus();
          inputElement.select();
        }
      }
      setActiveProductRow(0);
    }
  };

  const handleCommentKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    rowIndex: number
  ) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      let nextRowIndex: number | null = null;

      if (showPantryList) {
        // When pantry list is on, use simple navigation
        nextRowIndex = rowIndex + 1;
        if (nextRowIndex >= lines.length) {
          return;
        }
      } else {
        // When pantry list is off, navigate through filtered lines
        if (filteredLines.length === 0) return;

        const searchRow = filteredLines[0];
        const productRows = filteredLines.slice(1);
        const displayLines = [searchRow, ...productRows];

        // Find current row's position in filtered lines
        const currentLine = lines[rowIndex];
        if (!currentLine) return;

        const currentFilteredIndex = displayLines.findIndex(
          (l) => l.id === currentLine.id
        );

        if (
          currentFilteredIndex >= 0 &&
          currentFilteredIndex < displayLines.length - 1
        ) {
          const nextFilteredLine = displayLines[currentFilteredIndex + 1];
          // Find original index of next filtered line
          nextRowIndex = lines.findIndex((l) => l.id === nextFilteredLine.id);
          if (nextRowIndex === -1) {
            return;
          }
        } else {
          return;
        }
      }

      const nextInput = inputRefs.current[`${nextRowIndex}-comment`];
      if (nextInput) {
        nextInput.focus();
      }
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      let prevRowIndex: number | null = null;

      if (showPantryList) {
        // When pantry list is on, use simple navigation
        prevRowIndex = rowIndex - 1;
        if (prevRowIndex < 0) {
          return;
        }
      } else {
        // When pantry list is off, navigate through filtered lines
        if (filteredLines.length === 0) return;

        const searchRow = filteredLines[0];
        const productRows = filteredLines.slice(1);
        const displayLines = [searchRow, ...productRows];

        // Find current row's position in filtered lines
        const currentLine = lines[rowIndex];
        if (!currentLine) return;

        const currentFilteredIndex = displayLines.findIndex(
          (l) => l.id === currentLine.id
        );

        if (currentFilteredIndex > 0) {
          const prevFilteredLine = displayLines[currentFilteredIndex - 1];
          // Find original index of previous filtered line
          prevRowIndex = lines.findIndex((l) => l.id === prevFilteredLine.id);
          if (prevRowIndex === -1) {
            return;
          }
        } else {
          return;
        }
      }

      const prevInput = inputRefs.current[`${prevRowIndex}-comment`];
      if (prevInput) {
        prevInput.focus();
      }
    } else if (e.key === "Enter") {
      e.preventDefault();
      // Focus on the product name input of the search row (new line row)
      if (productInputRef.current) {
        const inputElement = productInputRef.current.querySelector("input");
        if (inputElement) {
          inputElement.focus();
          inputElement.select();
        }
      }
      setActiveProductRow(0);
    }
  };

  // Scroll selected product into view
  useEffect(() => {
    if (selectedProductRowRef.current) {
      selectedProductRowRef.current.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    }
  }, [selectedProductIndex, productResults]);

  // Product dropdown keyboard navigation
  useEffect(() => {
    if (activeProductRow === null || productResults.length === 0) {
      setSelectedProductIndex(0);
      return;
    }

    const handleKeyDown = (e: KeyboardEvent) => {
      // Only handle if product dropdown is visible
      const productDropdown = document.querySelector("[data-product-dropdown]");
      if (
        !productDropdown ||
        activeProductRow === null ||
        productResults.length === 0
      )
        return;

      if (e.key === "ArrowDown") {
        e.preventDefault();
        setSelectedProductIndex((prev) =>
          prev < productResults.length - 1 ? prev + 1 : prev
        );
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setSelectedProductIndex((prev) => (prev > 0 ? prev - 1 : 0));
      } else if (e.key === "Enter") {
        e.preventDefault();
        if (productResults[selectedProductIndex]) {
          handleProductPick(productResults[selectedProductIndex]);
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [activeProductRow, productResults, selectedProductIndex]);

  const removeLine = (index: number) => {
    // Don't allow removing the search row (index 0)
    if (index === 0) return;
    setLines((prev) => prev.filter((_, idx) => idx !== index));
  };

  const handleDeliveryDateChange = (date: Date | null) => {
    setDeliveryDate(date);
    setDeliveryDateWarning(null);

    if (date && selectedCustomer) {
      // Format date as ISO date string (YYYY-MM-DD) which .NET DateTime.TryParse can handle reliably
      const formatted = moment(date).format("YYYY-MM-DD");
      checkDeliveryDate.mutate(
        {
          customerId: selectedCustomer.customerAccountId ?? selectedCustomer.id,
          date: formatted,
          id: documentData?.id,
        },
        {
          onSuccess: (data) => {
            if (data?.status && data.status !== 0) {
              const message =
                data.errorMessage ||
                data.errorMessageToDisplay ||
                "Delivery date is not available";
              setDeliveryDateWarning(message);
              SnackbarUtils.warning(message);
            }
          },
        }
      );
    }
  };

  // Filter lines based on Pantry List switch
  const filteredLines = useMemo(() => {
    if (showPantryList) {
      // When switch is ON, show all lines as it currently does
      return lines;
    } else {
      // When switch is OFF, hide pantry list items but keep manually added items
      const searchRow = lines[0]; // Always keep search row
      const productRows = lines.slice(1);
      const filteredProductRows = productRows.filter((line) => {
        // If it's marked as ad-hoc (manually added), always show it
        if (line.isAdHoc) {
          return true;
        }

        // If it's from the initial document load (pantry list)
        if (line.id && initialLineIds.has(line.id)) {
          // Keep it only if it has a quantity greater than zero
          return line.quantity != null && Number(line.quantity) > 0;
        }

        // Default: show the line
        return true;
      });
      return [searchRow, ...filteredProductRows];
    }
  }, [lines, showPantryList, initialLineIds]);

  // Compute valid lines for submission (only from filtered lines)
  const validLines = useMemo(
    () =>
      filteredLines
        .slice(1) // Exclude search row (index 0)
        .filter(
          (line) =>
            (line.productSKU || line.productName) &&
            Number(line.quantity) > 0 &&
            Number(line.sellPrice) >= 0
        ),
    [filteredLines]
  );

  const totals = useMemo(() => {
    const subtotal = validLines.reduce(
      (acc, line) =>
        acc + (Number(line.sellPrice) || 0) * (Number(line.quantity) || 0),
      0
    );
    const deliveryFee = Number(
      documentData?.deliveryFeeAmount ??
        documentData?.defaultDeliveryFeeAmount ??
        0
    );
    const total = subtotal + deliveryFee;

    return {
      subtotal,
      deliveryFee,
      total,
    };
  }, [validLines, documentData]);

  const buildPayload = (): DocumentModel & { idempotencyKey: string } => {
    if (!selectedCustomer) {
      throw new Error("Please select a customer.");
    }
    if (!deliveryDate) {
      throw new Error("Please choose a delivery date.");
    }
    if (!validLines.length) {
      throw new Error("Please add at least one line with quantity.");
    }

    // Format delivery date as YYYY-MM-DD string to avoid timezone issues
    // The backend will parse this as a local date without timezone conversion
    const deliveryDateString = moment(deliveryDate).format("YYYY-MM-DD");

    const doc: DocumentModel = {
      ...documentData,
      customerAccountId:
        selectedCustomer.customerAccountId ??
        selectedCustomer.id ??
        documentData?.customerAccountId,
      deliveryDate: deliveryDateString as any, // Send as date string to avoid timezone conversion
      note,
      documentTypeId: documentData?.documentTypeId ?? DocumentTypeId.ORDER,
    };

    // Remove deliveryDateFormatted from payload - backend doesn't need it
    delete doc.deliveryDateFormatted;

    const mappedLines = validLines.map((line) => ({
      id: line.id,
      productId: line.productId,
      productSKU: line.productSKU, // Keep for type compatibility
      productSku: line.productSKU,
      productName: line.productName,
      unit: line.unitOfMeasure,
      quantity: Number(line.quantity) || 0,
      price: Number(line.sellPrice) || 0,
      taxCode: line.taxCode,
      taxPercentage: (Number(line.taxPercentage) || 0) / 100,
      comment: line.comment,
    }));

    return {
      ...doc,
      lines: mappedLines,
      idempotencyKey,
    };
  };

  const clearForm = () => {
    setSelectedCustomer(null);
    setDocumentData(null);
    setPantryProductIds([]);
    setFavouriteProductIds([]);
    setProductAgreementIds([]);
    setIsUseProductAgreement(false);
    setIsEditingDeliveryAddress(false);
    setEditableDeliveryAddress({
      line1: "",
      line2: "",
      suburb: "",
      state: "",
      postCode: "",
      country: "",
    });
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    setDeliveryDate(tomorrow);
    setDeliveryDateWarning(null);
    setNote("");
    setLines([createEmptyLine()]);
    setProductResults([]);
    setActiveProductRow(null);
    setProductSearchTerm("");
    setProductDropdownPosition(null);
    setShowPantryList(false);
    setInitialLineIds(new Set());
    setSelectedProductIndex(0);
    setIdempotencyKey(uuidv4()); // Generate new key for next order
  };

  const handleLoadPreviousCustomer = () => {
    if (!previousCustomer) return;

    // Directly use the stored customer data to populate the form
    handleSelectCustomer(previousCustomer);
  };

  const validateQuantityCells = (): boolean => {
    // Check if at least one line has a valid quantity
    // Only check lines that are currently visible (in filteredLines)
    const filteredLineIds = new Set(filteredLines.map((line) => line.id));

    const hasAtLeastOneValidItem = lines.some((line, index) => {
      // Skip the search row (index 0)
      if (index === 0) return false;

      // Only check lines that are currently visible (in filteredLines)
      if (!line.id || !filteredLineIds.has(line.id)) return false;

      // Check if the line has a product with valid quantity
      return (
        (line.productSKU || line.productName) &&
        line.quantity != null &&
        line.quantity > 0
      );
    });

    // If no valid items, show error
    if (!hasAtLeastOneValidItem) {
      SnackbarUtils.error("Please add at least one product with a quantity.");
      return false;
    }

    return true;
  };

  const handleSave = async () => {
    try {
      // Validate quantity cells first
      if (!validateQuantityCells()) {
        return;
      }

      const payload = buildPayload();
      const response = await saveDocument.mutateAsync(payload);
      if (!response?.success) {
        SnackbarUtils.error("Unable to save order.");
        return;
      }
      SnackbarUtils.success("Order saved successfully.");

      // Store customer data in state for next order (no persistence after refresh)
      if (selectedCustomer) {
        setPreviousCustomer(selectedCustomer);
      }

      // Clear the form after saving
      clearForm();

      // Focus customer field after form reset
      setTimeout(() => {
        customerLookupRef.current?.focus();
      }, 0);
    } catch (error: any) {
      SnackbarUtils.error(
        error?.message ?? "Please complete required information."
      );
    }
  };

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Only handle if Alt key is pressed
      if (!e.altKey) return;

      // Handle Alt+Backspace for removing rows
      if (
        e.key === "Backspace" &&
        focusedRowIndex !== null &&
        focusedRowIndex > 0
      ) {
        e.preventDefault();
        removeLine(focusedRowIndex);
        return;
      }

      // Alt+N: Focus product search
      if (e.key === "n" || e.key === "N") {
        e.preventDefault();
        const productInput = inputRefs.current["0-productName"];
        if (productInput) {
          productInput.focus();
          setActiveProductRow(0);
        }
        return;
      }

      // Alt+P: Toggle pantry list
      if (e.key === "p" || e.key === "P") {
        e.preventDefault();
        setShowPantryList((prev) => !prev);
        return;
      }

      // Alt+C: Focus customer search
      if (e.key === "c" || e.key === "C") {
        e.preventDefault();
        const customerInput = document.querySelector(
          "[data-customer-input] input"
        ) as HTMLInputElement;
        if (customerInput) {
          customerInput.focus();
          customerInput.select();
        }
        return;
      }

      // Alt+S: Save and New
      if (e.key === "s" || e.key === "S") {
        e.preventDefault();
        handleSave();
        return;
      }

      // Alt+R: Clear
      if (e.key === "r" || e.key === "R") {
        e.preventDefault();
        clearForm();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [focusedRowIndex, removeLine, handleSave, clearForm]);

  const isAnyApiLoading =
    getEmptyDocument.isLoading || checkDeliveryDate.isLoading;

  // Render status for Google Maps API loading
  const renderGoogleMapsStatus = () => {
    // Silent loading - return empty fragment since autocomplete is optional
    return <></>;
  };

  // Main component content
  const renderContent = () => (
    <div
      className={`p-1 space-y-3 relative ${
        props.isIframe ? "h-screen bg-white" : ""
      }`}
    >
      {!props.isIframe && (
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
          <div>
            <h1 className="text-3xl font-semibold text-neutral">Enter Order</h1>
            {previousCustomer && (
              <p className="text-sm text-neutral-60 mt-0.5">
                Last Ordered Customer:{" "}
                <button
                  type="button"
                  onClick={handleLoadPreviousCustomer}
                  className="font-medium text-blue hover:text-blue-dark hover:underline cursor-pointer"
                  disabled={
                    getEmptyDocument.isLoading || saveDocument.isLoading
                  }
                >
                  {previousCustomer.name}
                </button>
              </p>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button
              color="gray"
              variant="outline"
              onClick={clearForm}
              disabled={getEmptyDocument.isLoading || saveDocument.isLoading}
            >
              Clear
            </Button>
            <Button
              color="blue"
              onClick={handleSave}
              isLoading={saveDocument.isLoading}
              iconLeft={Icon.Checked}
              disabled={getEmptyDocument.isLoading || saveDocument.isLoading}
            >
              {saveDocument.isLoading ? "Saving..." : "Save and New"}
            </Button>
          </div>
        </div>
      )}
      {props.isIframe && (
        <div className="flex py-2 justify-between">
          <div className="flex items-center text-neutral-60 gap-1">
            {previousCustomer && (
              <>
                Last Ordered Customer:{" "}
                <button
                  type="button"
                  onClick={handleLoadPreviousCustomer}
                  className="font-medium text-blue hover:text-blue-dark hover:underline cursor-pointer"
                  disabled={
                    getEmptyDocument.isLoading || saveDocument.isLoading
                  }
                >
                  {previousCustomer.name}
                </button>
              </>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button
              color="gray"
              variant="outline"
              onClick={clearForm}
              disabled={getEmptyDocument.isLoading || saveDocument.isLoading}
            >
              Clear
            </Button>
            <Button
              color="blue"
              onClick={handleSave}
              isLoading={saveDocument.isLoading}
              iconLeft={Icon.Checked}
              disabled={getEmptyDocument.isLoading || saveDocument.isLoading}
            >
              {saveDocument.isLoading ? "Saving..." : "Save and New"}
            </Button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-3 lg:items-start">
        <div className="lg:col-span-4 space-y-2">
          <div className="bg-white border border-neutral-20 rounded p-3 shadow-sm">
            <div className="grid md:grid-cols-2 gap-3">
              <CustomerLookup
                ref={customerLookupRef}
                onSelect={handleSelectCustomer}
                selectedCustomer={selectedCustomer}
                disabled={getEmptyDocument.isLoading || saveDocument.isLoading}
                onClear={handleClearCustomer}
              />

              <DatePicker
                label="Delivery date"
                selected={deliveryDate}
                onChange={(date: Date) => handleDeliveryDateChange(date)}
                placeholder="Select delivery date"
                minDate={new Date()}
                isError={!deliveryDate}
                isWarning={Boolean(deliveryDateWarning)}
                errorMessage={deliveryDateWarning || undefined}
                disabled={getEmptyDocument.isLoading || saveDocument.isLoading}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2 md:items-start">
              <div>
                <label className="text-base text-neutral-80 mb-1 block">
                  Delivery address
                </label>
                {!isEditingDeliveryAddress ? (
                  <div className="p-3 border border-neutral-20 rounded bg-neutral-5 text-base text-neutral flex items-center justify-between">
                    <span>
                      {formatAddress(documentData, "delivery") || (
                        <span className="text-neutral-60">
                          No delivery address
                        </span>
                      )}
                    </span>
                    {selectedCustomer && (
                      <button
                        type="button"
                        tabIndex={-1}
                        className="ml-2 text-blue hover:text-blue-dark disabled:opacity-50 disabled:cursor-not-allowed"
                        onClick={() => {
                          // Populate editable fields from documentData
                          const doc = documentData as any;
                          setEditableDeliveryAddress({
                            line1: doc?.DeliveryAddressLine1 || "",
                            line2: doc?.DeliveryAddressLine2 || "",
                            suburb: doc?.DeliverySuburb || "",
                            state: doc?.DeliveryState || "",
                            postCode: doc?.DeliveryPostCode || "",
                            country: doc?.DeliveryCountry || "",
                          });
                          setIsEditingDeliveryAddress(true);
                        }}
                        title="Edit delivery address"
                        disabled={
                          getEmptyDocument.isLoading || saveDocument.isLoading
                        }
                      >
                        <Icon.Edit size="16" />
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="space-y-2 border border-neutral-20 rounded p-3 bg-white">
                    <GooglePlacesInput
                      placeholder="Address Line 1"
                      value={editableDeliveryAddress.line1}
                      onChange={(value) =>
                        setEditableDeliveryAddress({
                          ...editableDeliveryAddress,
                          line1: value,
                        })
                      }
                      onPlaceSelect={handlePlaceSelect}
                      countryRestriction="au"
                    />
                    <Input
                      placeholder="Address Line 2 (optional)"
                      value={editableDeliveryAddress.line2}
                      onChange={(e) =>
                        setEditableDeliveryAddress({
                          ...editableDeliveryAddress,
                          line2: e.target.value,
                        })
                      }
                    />
                    <div className="grid grid-cols-2 gap-2">
                      <Input
                        placeholder="Suburb"
                        value={editableDeliveryAddress.suburb}
                        onChange={(e) =>
                          setEditableDeliveryAddress({
                            ...editableDeliveryAddress,
                            suburb: e.target.value,
                          })
                        }
                      />
                      <Input
                        placeholder="State"
                        value={editableDeliveryAddress.state}
                        onChange={(e) =>
                          setEditableDeliveryAddress({
                            ...editableDeliveryAddress,
                            state: e.target.value,
                          })
                        }
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <Input
                        placeholder="Post Code"
                        value={editableDeliveryAddress.postCode}
                        onChange={(e) =>
                          setEditableDeliveryAddress({
                            ...editableDeliveryAddress,
                            postCode: e.target.value,
                          })
                        }
                      />
                      <Input
                        placeholder="Country"
                        value={editableDeliveryAddress.country}
                        onChange={(e) =>
                          setEditableDeliveryAddress({
                            ...editableDeliveryAddress,
                            country: e.target.value,
                          })
                        }
                      />
                    </div>
                    <div className="flex items-center gap-2 mt-2">
                      <Button
                        size="sm"
                        color="blue"
                        onClick={() => {
                          // Update documentData with new address
                          if (documentData) {
                            const updatedDoc = {
                              ...documentData,
                              deliveryAddressId: null, // Set to null to indicate custom address
                              DeliveryAddressLine1:
                                editableDeliveryAddress.line1,
                              DeliveryAddressLine2:
                                editableDeliveryAddress.line2,
                              DeliverySuburb: editableDeliveryAddress.suburb,
                              DeliveryState: editableDeliveryAddress.state,
                              DeliveryPostCode:
                                editableDeliveryAddress.postCode,
                              DeliveryCountry: editableDeliveryAddress.country,
                            };
                            setDocumentData(updatedDoc);
                          }
                          setIsEditingDeliveryAddress(false);
                        }}
                      >
                        Save
                      </Button>
                      <Button
                        size="sm"
                        color="gray"
                        variant="outline"
                        onClick={() => {
                          setIsEditingDeliveryAddress(false);
                          // Reset to original values
                          const doc = documentData as any;
                          setEditableDeliveryAddress({
                            line1: doc?.DeliveryAddressLine1 || "",
                            line2: doc?.DeliveryAddressLine2 || "",
                            suburb: doc?.DeliverySuburb || "",
                            state: doc?.DeliveryState || "",
                            postCode: doc?.DeliveryPostCode || "",
                            country: doc?.DeliveryCountry || "",
                          });
                        }}
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                )}
              </div>
              <div className="flex flex-col">
                <label className="text-base text-neutral-80 mb-1 block">
                  Notes
                </label>
                <textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  className="w-full border border-neutral-20 rounded p-3 text-base text-neutral focus:outline-none focus:ring-blue focus:border-blue disabled:bg-neutral-20 disabled:text-neutral-40 disabled:cursor-not-allowed"
                  style={{ height: "83px" }}
                  placeholder="Optional note visible on the order"
                  disabled={
                    getEmptyDocument.isLoading || saveDocument.isLoading
                  }
                />
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-3 h-full">
          <div className="bg-white border border-neutral-20 rounded p-4 shadow-sm h-full flex flex-col">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-xl font-semibold text-neutral">Summary</h3>
            </div>
            <dl className="space-y-3 text-lg flex-1">
              <div className="flex justify-between">
                <dt className="text-neutral-80">Subtotal</dt>
                <dd className="font-semibold text-neutral">
                  {formatCurrency(totals.subtotal)}
                </dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-neutral-80">Delivery fee</dt>
                <dd className="font-semibold text-neutral">
                  {formatCurrency(totals.deliveryFee)}
                </dd>
              </div>
              <div className="flex justify-between pt-3 border-t border-neutral-20 mt-auto">
                <dt className="text-neutral-100 font-semibold text-lg">
                  Total
                </dt>
                <dd className="text-2xl font-bold text-neutral">
                  {formatCurrency(totals.total)}
                </dd>
              </div>
            </dl>
          </div>
        </div>
      </div>

      <ProductTable
        lines={lines}
        filteredLines={filteredLines}
        productResults={productResults}
        showPantryList={showPantryList}
        validLines={validLines}
        selectedProductIndex={selectedProductIndex}
        productDropdownPosition={productDropdownPosition}
        focusedRowIndex={focusedRowIndex}
        productInputRef={productInputRef}
        inputRefs={inputRefs}
        selectedProductRowRef={selectedProductRowRef}
        justClosedDropdownRef={justClosedDropdownRef}
        isLoadingProducts={productLookup.isLoading}
        disabled={
          getEmptyDocument.isLoading ||
          saveDocument.isLoading ||
          !selectedCustomer
        }
        onShowPantryListChange={setShowPantryList}
        onLineChange={handleLineChange}
        onProductPick={handleProductPick}
        onAddProduct={handleAddProduct}
        onRemoveLine={removeLine}
        onSetActiveProductRow={setActiveProductRow}
        onSetProductSearchTerm={setProductSearchTerm}
        onSetProductResults={setProductResults}
        onSetProductDropdownPosition={setProductDropdownPosition}
        onSetSelectedProductIndex={setSelectedProductIndex}
        onSetFocusedRowIndex={setFocusedRowIndex}
        onQuantityKeyDown={handleQuantityKeyDown}
        onCommentKeyDown={handleCommentKeyDown}
      />
    </div>
  );

  return (
    <Wrapper
      {...GOOGLE_MAP_OPTIONS}
      callback={handleGoogleMapsLoad}
      render={renderGoogleMapsStatus}
    >
      {isAnyApiLoading && (
        <div className="fixed top-0 left-1/2 transform -translate-x-1/2 z-50 mt-2">
          <div className="bg-white border border-neutral-20 rounded shadow-lg px-4 py-2 flex items-center gap-2">
            <Icon.Loading size="16" className="text-blue animate-spin" />
            <span className="text-base text-neutral">
              {saveDocument.isLoading ? "Saving..." : "Loading..."}
            </span>
          </div>
        </div>
      )}
      {renderContent()}
    </Wrapper>
  );
};

export default EnterOrderForm;
